import React, {DragEventHandler, FC, lazy, ReactElement, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {connect} from "react-redux";
import './DocumentBlock.less';
import * as DateUtil from "@/lib/dateutils";
import util from "@/lib/util";
import IconButton from "@/ui/IconButton/IconButton";
import Icons from "@/ui/IconButton/Icons";
import DocumentEditor from "./DocumentEditor";
import {signDocuments} from "@/rest/api";
import {confirmError} from "@/ui/Confirm";
import Alert from '@/lib/alert';
import * as idb from '@/lib/idbd';
import * as DocumentActions from '@/store/actions/documentActions';
import {deleteDocument} from '@/store/deps/documentDeps';
import Document from '@/entities/document';
import StateLabel from '@/ui/StateLabel/StateLabel';
import * as TaskHelper from '@/TaskManagement/TaskHelper';
import CaseHelper from '@/Case/CaseHelper';
import LeadHelper from '@/CRM/Sales/LeadHelper';
import Searchbar, {SearchOptions} from '@/Search/Searchbar';
import TextField from '@/ui/TextField';
import i18n from '@/i18n/i18n';
import {Tooltip} from "@mui/material";
import find from "@/store/find";
import {formatUniqueName, formatUserNameFromId, getUserInitials} from "@/Contacts/ContactFormat";
import {getCurrentFirmId, getCurrentUserId, isPortalUser} from "@/lib/picos/currentLogin";
import {isMobileView} from "@/lib/picos/browserFns";
import Loading from "@/ui/Loading";
import {STYLES, STYLESHEET} from "@/ui/TextArea/TextArea";
import {existsFilter} from "@/lib/picos/filters";
import {isLiveNewerOrEqual} from "@/hooks/cachedEntity";
import {uuid} from "@/@types/schema";
import {EntityNames} from "@/entities";
import type {RootState} from "@/@types/store";
import ArrayMap from "@/lib/ArrayMap";
import {MiscState} from "@/store/reducers/miscReducer";
import Contact from "@/entities/contact";
import {EditableTableCustomAddLabel, EditableTableOtherAction, EditableTablePropertyEntry, EditableTableProps, EditableTableSortEntry, Justify} from "@/ui/EditableTable/EditableTable";
import useIsMounted from "@/hooks/useIsMounted";
import {VoidBoolCallback, VoidCallback} from "@/@types/functions";
import {useRootDispatch} from "@/hooks/useOutlawStore";
import {downloadLocal, downloadRemote, fetchV1RemoteContent, friendlyContentType, getDropFiles, getFileName, getIcon, getRelatedDocumentsForEntity, processFiles} from "./documentHelper";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {ContentBlockChildProps} from "@/ui/ContentBlock/ContentBlock";
import useComponentCache from "@/hooks/useComponentCache";
import useEditContribution from "@/hooks/useEditContribution";

type DocumentBlockState = {
    startWithNew: boolean;
    startWith: Document | null;
    dropFiles: Document[] | null;
    isEditing: boolean;
    searchTerms: string[];
    searchOptions: SearchOptions;
}

const EditableTable = lazy(() => import('@/ui/EditableTable/EditableTable'));

const FIVE_MIN = 300_000_000; // 5 minutes in microseconds
const DEFAULT_DATA = [];

const htmlMap = new Map<string, string>();
htmlMap.set('ql-font-andalemono', `@import url('https://fonts.cdnfonts.com/css/andale-mono'); .ql-font-andalemono, .ql-font span[data-value="andalemono"]::before { font-family: "Andale Mono", monospace; }`);
htmlMap.set('ql-font-arial', `@font-face {font-family: 'Helvetica, sans-serif', src: local('Helvetica'); }; .ql-font-arial, .ql-font span[data-value="arial"]::before { font-family: Helvetica, sans-serif; }`);
htmlMap.set('ql-font-helvetica', `@font-face {font-family: 'Helvetica, sans-serif', src: local('Helvetica'); }; .ql-font-helvetica, .ql-font span[data-value="helvetica"]::before { font-family: Helvetica, sans-serif; }`);
htmlMap.set('ql-font-comicsansms', `@import url('https://fonts.cdnfonts.com/css/calibri'); .ql-font-calibri, .ql-font span[data-value="calibri"]::before { font-family: Calibri, sans-serif; }`);
htmlMap.set('ql-font-couriernew', `@import url('https://fonts.cdnfonts.com/css/comic-sans'); .ql-font-comicsansms, .ql-font span[data-value="comicsansms"]::before { font-family: "Comic Sans MS", cursive, sans-serif; }`);
htmlMap.set('ql-font-couriernew', `@import url('https://fonts.cdnfonts.com/css/courier-new'); .ql-font-couriernew, .ql-font span[data-value="couriernew"]::before { font-family: "Courier New", Courier, monospace; }`);
htmlMap.set('ql-font-frederikathegreat', `@import url('https://fonts.googleapis.com/css2?family=Fredericka+the+Great&display=swap'); .ql-font-frederikathegreat, .ql-font span[data-value="frederikathegreat"]::before { font-family: "Frederika the Great", cursive; }`);
htmlMap.set('ql-font-georgia', `@import url('https://fonts.cdnfonts.com/css/georgia'); .ql-font-georgia, .ql-font span[data-value="georgia"]::before { font-family: Georgia, serif; }`);
htmlMap.set('ql-font-nunito', `@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap'); .ql-font-nunito, .ql-font span[data-value="nunito"]::before { font-family: Nunito, sans-serif; }`);
htmlMap.set('ql-font-impact', `@import url('https://fonts.googleapis.com/css2?family=Impact&display=swap'); .ql-font-impact, .ql-font span[data-value="impact"]::before { font-family: Impact, Charcoal, sans-serif; }`);
htmlMap.set('ql-font-inconsolata', `@import url('https://fonts.googleapis.com/css2?family=Inconsolata&display=swap'); .ql-font-inconsolata, .ql-font span[data-value="inconsolata"]::before { font-family: Inconsolata, monospace; }`);
htmlMap.set('ql-font-lucidasans', `@import url('https://fonts.cdnfonts.com/css/lucida-sans'); .ql-font-lucidasans, .ql-font span[data-value="lucidasans"]::before { font-family: "Lucida Sans","Lucida Grande", sans-serif; }`);
htmlMap.set('ql-font-lucidaconsole', `@import url('https://fonts.cdnfonts.com/css/lucida-console'); .ql-font-lucidaconsole, .ql-font span[data-value="lucidaconsole"]::before { font-family: "Lucida Console", Monaco, monospace; }`);
htmlMap.set('ql-font-opensans', `@import url('https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap'); .ql-font-opensans, .ql-font span[data-value="opensans"]::before { font-family: "Open Sans", sans-serif; }`);
htmlMap.set('ql-font-palatinolinotype', `@import url('https://fonts.cdnfonts.com/css/palatino-linotype'); .ql-font-palatinolinotype, .ql-font span[data-value="palatinolinotype"]::before { font-family: "Palatino Linotype", sans-serif; }`);
htmlMap.set('ql-font-poppins', `@import url("https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"); .ql-font-poppins, .ql-font span[data-value="poppins"]::before { font-family: Poppins, sans-serif; }`);
htmlMap.set('ql-font-spectral', `@import url('https://fonts.googleapis.com/css2?family=Spectral+SC&display=swap'); .ql-font-spectralsc, .ql-font span[data-value="spectralsc"]::before { font-family: "Spectral SC", serif; }`);
htmlMap.set('ql-font-tahoma', `@import url('https://fonts.cdnfonts.com/css/tahoma'); .ql-font-tahoma, .ql-font span[data-value="tahoma"]::before { font-family: Tahoma, Geneva, sans-serif; }`);
htmlMap.set('ql-font-timesnewroman', `@import url('https://fonts.cdnfonts.com/css/times-new-roman'); .ql-font-timesnewroman, .ql-font span[data-value="timesnewroman"]::before { font-family: "Times New Roman", Times, serif; }`);
htmlMap.set('ql-font-trebuchetms', `@import url('https://fonts.cdnfonts.com/css/trebuchet-ms-2'); .ql-font-trebuchetms, .ql-font span[data-value="trebuchetms"]::before { font-family: "Trebuchet MS", Helvetica, sans-serif; }`);
htmlMap.set('ql-font-verdana', `@import url('https://fonts.cdnfonts.com/css/verdana'); .ql-font-verdana, .ql-font span[data-value="verdana"]::before { font-family: Verdana, sans-serif; }`);
htmlMap.set('ql-size-8pt', `{ font-size: 8pt !important;}`);
htmlMap.set('ql-size-9pt', `{ font-size: 9pt !important;}`);
htmlMap.set('ql-size-10pt', `{ font-size: 10pt !important;}`);
htmlMap.set('ql-size-11pt', `{ font-size: 11pt !important;}`);
htmlMap.set('ql-size-12pt', `{ font-size: 12pt !important;}`);
htmlMap.set('ql-size-13pt', `{ font-size: 13pt !important;}`);
htmlMap.set('ql-size-14pt', `{ font-size: 14pt !important;}`);
htmlMap.set('ql-size-16pt', `{ font-size: 16pt !important;}`);
htmlMap.set('ql-size-18pt', `{ font-size: 18pt !important;}`);
htmlMap.set('ql-size-20pt', `{ font-size: 20pt !important;}`);
htmlMap.set('ql-size-22pt', `{ font-size: 22pt !important;}`);
htmlMap.set('ql-size-24pt', `{ font-size: 24pt !important;}`);
htmlMap.set('ql-size-28pt', `{ font-size: 28pt !important;}`);
htmlMap.set('ql-size-30pt', `{ font-size: 30pt !important;}`);

/**
 * Wrap a document node with full html and css.
 *
 * @param {Document} doc
 * @param {string} data
 * @returns {string}
 */
const wrapHtml = (doc: Document, data: string): string => {
    const usedStyles: string[] = [];
    htmlMap.forEach((value, key) => {
        if (data.includes(key)) usedStyles.push(value);
    });

    return `
<html lang="en">
    <head title="${getFileName(doc)}">
        ${STYLESHEET}
        <style>
            body { width: 8in; margin: .5in; padding: 0; font-family: "Helvetica", Arial, sans-serif;}
            strong { font-weight: 600; };
            
            ${usedStyles.join('\n')}
        </style>
        <title>${getFileName(doc)}</title>
    </head>
    <body class="ql-editor">${data}</body>
</html>`.trim();
}

/**
 * Download a file given a remote URL
 *
 * @param {Document} doc
 */
const downloadRemoteHTML = async (doc: Document) => {
    if (!doc.uri) {
        // Get a signed URI
        const docs: Document[] = await signDocuments([doc]);
        if (docs.length > 0) doc.uri = docs[0].uri;
        else throw new Error('Could not fetch remote document');
    }

    const response = await fetch(doc.uri!);
    if (response.status >= 400) throw new Error(`Could not fetch remote document: ${response.statusText}`);

    // Pull the doc from its url, and wrap it in real html with a link to quill's f'd up CSS
    const text = await response.text();
    const starting = text.trimStart().substring(0, 20).toLowerCase();
    const noWrap = starting.startsWith('<html') || starting.startsWith('<!doctype html>');
    const alreadyWrapped = text.includes(STYLES);

    const documentData = noWrap || alreadyWrapped ? text : wrapHtml(doc, text);

    // Launch the download
    downloadLocal(getFileName(doc), documentData, doc.contentType ?? '');
};

const DEFAULT_SEARCH_OPTIONS: SearchOptions = {matchAll: true};
/**
 * This is a ContentBlock that handles documents (and thus notes) for any entity.
 *
 * It uses an EditableTable to display/edit the documents
 */
const DocumentBlock: FC<DocumentBlockProps> = props => {
    const key = useMemo(() => `document-block-${props.entityName}-${props.entityId}`, [props.entityId, props.entityName]);

    const propsOnBeforeEdit = props.onBeforeEdit;

    const isMounted = useIsMounted();

    const {cacheSave, cacheValue} = useComponentCache<DocumentBlockState>(key);

    const dispatch = useRootDispatch();

    const [startWithNew, setStartWithNew] = useState<boolean>(cacheValue('startWithNew', false));
    // A cached document is a snapshot. Prefer the live one unless the cached copy is newer
    // (a recovered or unsaved draft), so a colleague's save is not hidden on remount (OWA-526).
    const [startWith, setStartWith] = useState<Document | null>(() => {
        const cached = cacheValue('startWith') ?? null;
        const live = cached ? props.documents.get(cached.id) : undefined;
        return live && isLiveNewerOrEqual(live, cached) ? live : cached;
    });
    const [dropFiles, setDropFiles] = useState<Document[] | null>(cacheValue('dropFiles') ?? null);
    const [isEditing, setIsEditing] = useState<boolean>(cacheValue('isEditing', false));
    const [searchTerms, setSearchTerms] = useState<string[]>(cacheValue('searchTerms', []));
    const [searchOptions, setSearchOptions] = useState<SearchOptions>(cacheValue('searchOptions', DEFAULT_SEARCH_OPTIONS));

    useLayoutEffect(() => {
        return () => cacheSave({dropFiles, isEditing, startWith, startWithNew, searchTerms, searchOptions});
    }, [cacheSave, dropFiles, isEditing, searchOptions, searchTerms, startWith, startWithNew]);

    // DocumentBlock shows at most one editor at a time, so its contribution to
    // the parent's subEditActive counter (which feeds the card close-guard) is a
    // single unit, opened by a user add/edit. useEditContribution keeps that contribution idempotent and, if
    // this block unmounts while the editor is still open (ContentBlock viewport/
    // tab teardown, collapse, ErrorBoundary swallow), unwinds it so the parent
    // counter can't drift up and strand the card "save or close the document"
    // with nothing on screen. See DocumentBlock.subEditDrift.test.tsx.
    const {begin: beginParentEdit, end: endParentEdit} = useEditContribution(propsOnBeforeEdit, props.onEndEdit);

    const docs = getRelatedDocumentsForEntity(props.entityId, props.entityName);

    const queryKey = useMemo(() => [
        `documents-${props.entityId}`,
        props.documents.maxAddedOn,
        props.documents.maxDbAddedOn,
        props.misc.isConnected,
        JSON.stringify(searchTerms),
        JSON.stringify(searchOptions)
    ], [props.entityId, props.documents.maxAddedOn, props.documents.maxDbAddedOn, props.misc.isConnected, searchTerms, searchOptions]);

    const queryFn = useCallback(async () => {
        const docs = getRelatedDocumentsForEntity(props.entityId, props.entityName)
            .filter(doc => {
                // Now filter them based on searchProps (if any)
                if (!searchTerms || searchTerms.length === 0 || searchTerms[0] === '') return true;
                else return doc.search(searchTerms, searchOptions);
            });

        return docs && docs.length > 0 ? await signDocuments(docs) : DEFAULT_DATA;
    }, [props.entityId, props.entityName, searchTerms, searchOptions]);

    const documentsQuery = useQuery({
        queryKey,
        queryFn,
        placeholderData: keepPreviousData || DEFAULT_DATA
    });

    // Internationalization
    const intl = i18n.getModule('document');

    /**
     * Set the startWith parameter in state
     *
     * @param {Document} doc
     */
    const _setStartWith = useCallback((doc: Document) => {
        if (isMounted()) {
            beginParentEdit();
            setStartWith(doc);
        }
    }, [isMounted, beginParentEdit, setStartWith]);

    // Deep link: open a specific document when documentId is set and data is loaded
    const lastHandledDocumentId = useRef<string | null>(null);
    useEffect(() => {
        if (!props.documentId || lastHandledDocumentId.current === props.documentId) return;

        const data = documentsQuery.data;
        if (!data || data === DEFAULT_DATA) return;

        const doc = data.find(d => d.id === props.documentId);
        if (doc) {
            lastHandledDocumentId.current = props.documentId;
            // Defer to avoid cascading renders within this effect
            setTimeout(() => _setStartWith(doc), 0);
        }
    }, [props.documentId, documentsQuery.data, _setStartWith]);

    // OWA-939: unsaved document drafts are no longer reopened automatically; they
    // are recovered from the Dashboard's "Unsaved drafts" panel (Save or Attach to).

    /**
     * Returned from add action, receives a lead
     */
    const onAdd = useCallback((doc: Document | Document[], cb?: VoidBoolCallback): Promise<boolean> => {
        const docs = Array.isArray(doc) ? doc : [new Document({...doc})];
        const queued = docs.map(item => {
            const _doc = item.clone();
            _doc.entityId = props.entityId;
            _doc.entityType = props.entityName;
            _doc.firmId = getCurrentFirmId();
            _doc.addedBy = getCurrentUserId();
            if (!_doc.fileName) _doc.fileName = 'Untitled';

            return _doc;
        });
        return new Promise(resolve => dispatch(DocumentActions.saveManyDocumentsQueue(queued, saved => {
            if (saved) {
                setStartWith(null);
                setStartWithNew(false);
                setDropFiles(null);
                cb?.(false);
            }
            else Alert.addSimpleError('This document could not be saved on this device. Keep the editor open and preserve your notes or file before trying again.');
            resolve(saved);
        })));
    }, [setStartWith, setStartWithNew, setDropFiles, props.entityId, props.entityName, dispatch]);

    /**
     * @param {Document} doc
     * @returns {boolean}
     */
    const canEdit = useCallback((doc: Document): boolean => {
        if (props.readOnly) return false;

        // If this is a remote item that needs to be loaded first, make sure it has a URI!
        else if (!doc.local && (['application/pdf', 'message/rfc822'].includes(doc.contentType ?? '') || doc.contentType?.startsWith('text/'))) {
            // If this is a new text note, and we have contents available, we are good
            return !!(doc.uri || doc.buffer || doc.fileHandle);
        }
        else return true;
    }, [props.readOnly]);

    /**
     * @param {Document} doc
     * @returns {boolean}
     */
    const canDelete = useCallback((doc: Document): boolean => {
        // Only allow deletion of direct notes/files
        if (props.readOnly &&
            doc.addedBy === getCurrentUserId() &&
            (util.newHRTimestamp() - doc.addedOn) < FIVE_MIN
        ) return true;

        return !props.readOnly && (doc.entityType === props.entityName && doc.entityId === props.entityId);
    }, [props.readOnly, props.entityName, props.entityId])

    /**
     * Edit a text file or document.
     */
    const onEdit = useCallback(async (doc: Document, cb?: VoidBoolCallback): Promise<boolean> => {
        const docClone = Array.isArray(doc) ? doc[0].clone() : doc.clone();
        docClone.addedBy = getCurrentUserId();
        docClone.addedOn = util.newHRTimestamp();
        docClone.fatalError = null;
        docClone.entityType = props.entityName;
        docClone.entityId = props.entityId;
        docClone.status = 'U';

        if (!docClone.fileName) docClone.fileName = 'Untitled';

        const blocked = await idb.blockingDocumentUpload(docClone);
        if (blocked.isErr()) {
            Alert.addSimpleError('This document could not be checked for unsent work. Keep the editor open and try again.');
            return false;
        }
        if (blocked.unwrap() === 'failed') {
            Alert.addSimpleError('This document has an earlier upload that needs attention or is retrying. Open Home → Needs Attention; if it is not listed, use Support → Cache Management to inspect or download it before changing this document.');
            return false;
        }
        if (blocked.unwrap() === 'pending') {
            Alert.addSimpleError('This document still has unsent content. Wait for its upload to finish before changing it here; if it fails, check Home → Needs Attention or Support → Cache Management.');
            return false;
        }

        return new Promise(resolve => dispatch(DocumentActions.saveDocumentQueue(docClone, saved => {
            if (saved) {
                setStartWith(null);
                setStartWithNew(false);
                setDropFiles(null);
                // cb is optional — onLock/onUnlock call onEdit with no follow-up callback. (OWA-582)
                cb?.(false);
            }
            else Alert.addSimpleError('This document could not be saved on this device. Keep the editor open and preserve your notes or file before trying again.');
            resolve(saved);
        })));
    }, [props.entityName, props.entityId, dispatch, setStartWith, setStartWithNew, setDropFiles]);

    /**
     * @param {Document} doc
     * @param {function} cb
     */
    const onDelete = useCallback((doc: Document, cb: VoidCallback) => {
        deleteDocument(doc);

        if (typeof (cb) === 'function') cb();
    }, []);

    const onLock = useCallback((doc: Document) => {
        const locked = doc.clone();
        locked.private = true;
        onEdit(locked);
    }, [onEdit]);

    const onUnlock = useCallback((doc: Document) => {
        const unlocked = doc.clone();
        unlocked.private = false;
        onEdit(unlocked);
    }, [onEdit]);

    /**
     * Take a local or remote document and download it to the local computer.
     *
     * Stable useCallback (it reads only module-scope helpers and its `doc`
     * argument) so the downloadOption memo below can list it as an honest
     * dependency. As a bare arrow it was recreated every render while the
     * memo's only dep was a runtime-constant i18n string — permanently
     * freezing the first-render closure (OWA-518, same defect class as
     * OWA-516/OWA-517; benign today only because nothing here reads
     * component state).
     *
     * @async
     * @param {Document} doc
     */
    const onDownload = useCallback(async (doc: Document) => {
        // The easiest method is to handle a local document.
        if (doc.local) {
            // Assign an extension if we know the type and it doesn't already have one
            let ext = '';
            if (doc.contentType === 'text/plain' && !getFileName(doc).endsWith('.txt')) ext = '.txt';
            else if (doc.contentType === 'text/html' && !getFileName(doc).endsWith('.html')) ext = '.html';

            let documentData = doc.buffer || doc.plainText || '';

            if (doc.contentType === 'text/html' && ext === '.html') {
                const starting = documentData.trimStart().substring(0, 15).toLowerCase();
                const noWrap = (ext === '.html') && (starting.startsWith('<html') || starting.startsWith('<!doctype html>'));
                const alreadyWrapped = documentData.includes(STYLES);
                documentData = (noWrap || alreadyWrapped) ? documentData : wrapHtml(doc, documentData);
            }

            downloadLocal(getFileName(doc) + ext, documentData, doc.contentType ?? '');
        }
        else if (!doc.local && doc.uri) {
            // Try fetching as V1 (base64 text in S3) — auto-detects via content
            const displayDoc = await fetchV1RemoteContent(doc);
            if (displayDoc) {
                downloadLocal(getFileName(doc), displayDoc.buffer ?? '', doc.contentType ?? '');
            }
            else {
                downloadRemote(getFileName(doc), doc.uri);
            }
        }
        else {
            // Remote document without URI (e.g. HTML note)
            await downloadRemoteHTML(doc);
        }
    }, []);

    /**
     * Get the entity name this was attached to, and it's parents up to this document's entityId.
     *
     * For example, if this DocumentBlock is for a matter, we might find a document for a task associated with
     * one of the matter's clients.  That would return:
     *
     * [CONTACT NAME]:TASK
     *
     * @param {Document} doc
     * @return {ReactElement|string}
     */
    const getOrigin = useCallback((doc: Document | null | undefined): string | ReactElement | null => {
        // No document? Nothing to show
        if (!doc) return '';

        // Get the document's metadata; provide defaults if new
        const type = doc.entityType || props.entityName;
        const id = doc.entityId || props.entityId;
        // const thisType = props.entityName;
        const thisId = props.entityId;

        if (id === thisId) return null;

        switch (type) {
            case 'task':
                const task = TaskHelper.getTaskById(id);
                if (task) return <IconButton type={Icons.TASKS}
                                             label={task.title}
                                             color='green'
                                             textColor='green'
                />;
                else return null;
            case 'contact':
                const contact = find<Contact>('contacts', id);
                if (contact) return <IconButton type={Icons.ADDRESS_BOOK}
                                                label={formatUniqueName(contact)}
                                                color='#3a6172'
                                                textColor='#3a6172'
                />;
                else return null;
            case 'lead':
                const lead = LeadHelper.getLeadFromId(id);
                if (lead) return <IconButton type={Icons.LEAD}
                                             label={lead.name}
                                             color='mintgreen'
                                             textColor='mintgreen'
                />;
                else return null;
            case 'matter':
                const matter = CaseHelper.getMatterFromId(id);
                if (matter) return <IconButton type={Icons.CASE}
                                               label={matter.name}
                                               color='saddlebrown'
                                               textColor='saddlebrown'
                />;
                else return null;
            default:
                return <IconButton type={Icons.QUESTION} label='Unknown' color='red' textColor='red'/>;
        }
    }, [props.entityId, props.entityName]);

    const renderFileSize = useCallback((doc: Document) => {
        if (doc.fileSize < 1024) return `${doc.fileSize || 0}b`;
        if (doc.fileSize < 1024 * 1024) return `${(doc.fileSize / 1024 || 0).toFixed(0)}k`;
        else return `${Math.ceil((doc.fileSize / 1024 / 1024 || 0)).toFixed(0)}m`;
    }, []);

    const renderContentType = useCallback((doc: Document) => {
        return friendlyContentType(doc.contentType);
    }, []);

    const renderCreated = useCallback((doc: Document) => {
        const user = find('users', doc.addedBy);
        const addedBy = user ? getUserInitials(doc.addedBy, 3) : formatUserNameFromId(doc.addedBy);
        const createdOn = doc.createdOn ? DateUtil.formatDateTime(doc.createdOn, true) : '';
        return createdOn + ' by ' + addedBy;
    }, []);

    const renderLastModified = useCallback((doc: Document) => {
        const user = find('users', doc.addedBy);
        const addedBy = user ? getUserInitials(doc.addedBy, 3) : formatUserNameFromId(doc.addedBy);
        return DateUtil.formatDateTime(util.hrtToDate(doc.addedOn), true) + ' by ' + addedBy;
    }, []);

    /**
     * This is critical!  If e.preventDefault() is not called, it will just open the document in the current browser window.
     *
     * @param e
     */
    const onDragOver: DragEventHandler<HTMLDivElement> = useCallback(e => {
        e.preventDefault();
    }, []);

    /**
     * If files are dropped on to DocumentBlock (anywhere), then we will use EditableTable's startWithNew property
     * and the env property to pass through the files we found.  This function will get the files from the dataTransfer,
     * and set startWithNew to true in state.  The files will also be set in state, and passed through using
     * env.dropFiles.  When EditableTable notices the openWithNew property, it will immediately open up a new
     * editor.  We also pass param: 'file' in EditableTable, so it knows to open up the file attacher.
     *
     * Now the critical part:  in DocumentEditor, it is checking to see if props.env.dropFiles exists (but not in state).
     * If so, it checks state.dropHandled to ensure that it isn't already handling it.  Now it waits for the re-render,
     * then at the end of the render a setTimeout is fired calling processFiles with props.env.dropFiles.
     *
     * Ta-da!  Fucking magic.  This took most of the afternoon. Addendum: getting recursive directory adding took another afternoon.
     */
    const onDrop: DragEventHandler<HTMLDivElement> = useCallback(e => {
        // Prevent default behavior (Prevent file from being opened)
        e.preventDefault();

        // Don't want this called if DocumentEditor is open
        if (isEditing) return;

        getDropFiles(e)
            .then(({dropFiles, docs}) => {
                // Handle drop files (already saved)
                if (dropFiles.length > 0) {
                    processFiles(dropFiles, props.entityName, props.entityId)
                        .catch(err => {
                            console.error(err.message);
                        });
                }

                // Handle documents (directly created)
                if (docs.length > 0) {
                    docs.forEach(doc => {
                        doc.entityType = props.entityName;
                        doc.entityId = props.entityId;
                        doc.addedBy = getCurrentUserId();
                        doc.addedOn = util.newHRTimestamp();
                        dispatch(DocumentActions.saveDocumentQueue(doc));
                    });
                }
            })
            .catch(err => {
                confirmError('Error adding files', err.message);
            });
    }, [isEditing, props.entityName, props.entityId, dispatch]);

    /**
     * Set editing flag when opening.
     */
    const onBeforeEdit = useCallback(() => {
        setIsEditing(true);
        beginParentEdit();
    }, [beginParentEdit, setIsEditing]);

    /**
     * When we are done editing, need to reset the startsWithNew to false!  Otherwise it will just keep adding files...
     * This naturally fires after an add or an edit.
     */
    const onEndEdit = useCallback(() => {
        setIsEditing(false);
        setStartWith(null);
        setStartWithNew(false);
        setDropFiles(null);

        endParentEdit();
    }, [endParentEdit, setDropFiles, setIsEditing, setStartWith, setStartWithNew]);

    const onSearch = useCallback((searchTerms: string[], options: SearchOptions) => {
        setSearchTerms(searchTerms);
        setSearchOptions(options);
    }, [setSearchOptions, setSearchTerms]);

    /**
     * Get the correct icon for the type.
     *
     * @param {Document} doc
     * @returns {ReactElement}
     */
    const renderIcon = useCallback((doc: Document): ReactElement => {
        if (doc.contentType?.startsWith('image/') && doc.fileSize < 500 * 1024) {
            if (doc.uri) return <Tooltip title={getFileName(doc)}>
                <img alt='icon' src={doc.uri} width={'24px'}/>
            </Tooltip>;
            else {
                // Have to package up
                const content = doc.buffer || doc.plainText;
                if (!content) return <IconButton type={getIcon(doc.contentType)}/>;

                const src = `data:${doc.contentType};base64,${content}`;

                return <Tooltip title={getFileName(doc)}><img alt={''} src={src} width={'24px'}/></Tooltip>;
            }
        }

        return <IconButton type={getIcon(doc.contentType ?? '')}/>;
    }, []);

    /**
     *
     * @param {Document} doc
     * @returns {ReactElement|string}
     */
    const renderPrivate = useCallback((doc: Document): string | ReactElement => {
        return doc.private ? <IconButton type={Icons.HIDE}
                                         title={intl.d_private}
                                         size={Icons.IconSizeSmall}
                                         color='rgba(255,0,0,.6)'/> : '';
    }, [intl.d_private]);

    /**
     * Render the file name.
     *
     * @param {Document} doc
     * @returns {ReactElement}
     */
    const renderFileName = useCallback((doc: Document): ReactElement => {
        // This only works on files with readable content
        const showSnippet = (doc.plainText?.length ?? 0) > 10; //['text/plain', 'text/html'].includes(doc.contentType);

        let contents = '';
        if (showSnippet) {
            if (doc.contentType === 'text/html') {
                contents = doc.plainText || TextField.stripHTML(doc.buffer ?? '', []);
            }
            else contents = doc.plainText || doc.buffer || '';
        }

        let name: string;
        if (doc.nameChange && doc.dbAddedOn) name = doc.newFileName ?? '';
        else name = doc.fileName?.trim() || 'Untitled';

        const document = documentsQuery.data?.find(d => d.id === doc.id) || docs.find(d => d.id === doc.id);
        const origin = getOrigin(document) || '';

        // containerWidth is an optional layout prop; `!` preserves the existing
        // undefined→NaN slice/ellipsis behaviour rather than substituting 0. (OWA-554)
        const letters = Math.floor(props.containerWidth! / 5);
        let _snippet: string | Uint8Array = contents.slice(0, letters) || '';
        if (typeof (_snippet) !== 'string') _snippet = Buffer.from(_snippet).toString('utf8');
        const more = contents.length > letters ? '…' : '';

        const snippet = _snippet.trim().replaceAll(/(\n)+/g, '\n')
            .replaceAll('\n', ' ↩ ').toString();

        return <div>
            <div><StateLabel item={document!}>{origin} {name}</StateLabel></div>
            <div style={{
                fontWeight: 200,
                marginTop: '2px',
                maxHeight: '3em',
                overflow: 'hidden',
                // paddingBottom: '5px',
                fontSize: '.8em'
            }}
            >
                {snippet}{more}
            </div>
        </div>;
    }, [documentsQuery.data, docs, getOrigin, props.containerWidth]);

    const excludeOption: EditableTableOtherAction<Document> = useMemo(() => ({
        label: intl.d_excludeFromCaseFile,
        selectedIcon: Icons.HIDE,
        action: onLock,
        color: 'red',
        showIf: (doc: Document) => !doc.private
    }), [intl.d_excludeFromCaseFile, onLock]);

    const includeOption: EditableTableOtherAction<Document> = useMemo(() => ({
        label: intl.d_includeInCaseFile,
        selectedIcon: Icons.VIEW,
        action: onUnlock,
        color: 'darkgreen',
        showIf: (doc: Document) => doc.private
    }), [intl.d_includeInCaseFile, onUnlock]);

    const downloadOption: EditableTableOtherAction<Document> = useMemo(() => ({
        label: intl.d_download,
        selectedIcon: Icons.DOWNLOAD_LIGHT,
        showIf: doc => Array.isArray(doc) ? doc[0].local || !!doc[0].uri : doc.local || !!doc.uri,
        action: onDownload,
        color: 'var(--sl-color-blue-500)'
    }), [intl.d_download, onDownload]);

    const {containerHeight, containerWidth, isExpanded} = props;

    // If we have an autosaved entry, has it changed?
    let hasChanged = false;

    if (startWith && !startWith.waitingToSave) {
        const cur = startWith;
        const old = props.documents.get(cur.id);
        hasChanged = old ? !util.deepCompare(cur, old) : true;
    }

    let otherActions: EditableTableOtherAction<Document>[] = useMemo(() => isPortalUser() ? [downloadOption] : [excludeOption, includeOption, downloadOption],
        [downloadOption, excludeOption, includeOption]
    );

    const customAddLabels: EditableTableCustomAddLabel[] = useMemo(() => [
        {
            icon: Icons.ATTACHMENT,
            label: isMobileView() ? null : intl.d_files,
            param: 'file',
            title: intl.h_files
        },
        props.filesOnly || props.misc.portalUser ? null : {
            icon: Icons.FILE_TEXT,
            label: isMobileView() ? null : intl.d_note,
            param: 'plain',
            title: intl.h_note
        },
        props.filesOnly || props.misc.portalUser ? null : {
            icon: Icons.FILE_HTML,
            label: isMobileView() ? null : intl.d_fancy,
            param: 'html',
            color: 'navy',
            title: intl.h_fancy
        }
    ].filter(existsFilter), [intl.d_fancy, intl.d_files, intl.d_note, intl.h_fancy, intl.h_files, intl.h_note, props.filesOnly, props.misc.portalUser]);

    const properties = useMemo(() => {
        return [
            {
                label: 'icon',
                hideLabel: true,
                width: 'max-content',
                renderItemFn: renderIcon
            },
            !isPortalUser() && (documentsQuery.data || docs).some(doc => doc.private) && {
                label: 'private',
                displayLabel: <IconButton type={Icons.VIEW} title={intl.d_documentHidden} size='small'/>,
                width: 'max-content',
                renderItemFn: renderPrivate
            },
            {
                label: 'name',
                width: '1fr',
                renderItemFn: renderFileName
            },
            {
                label: 'version',
                justify: Justify.CENTER,
                width: 'max-content',
                showAtWidth: 1200,
            },
            {
                label: 'protocolVersion',
                displayLabel: 'Protocol',
                justify: Justify.CENTER,
                width: 'max-content',
                showAtWidth: 1200,
            },
            {
                label: 'fileSize',
                displayLabel: 'Size',
                width: 'max-content',
                justify: Justify.CENTER,
                renderItemFn: renderFileSize
            },
            {
                label: 'contentType',
                displayLabel: 'Type',
                width: 'max-content',
                showAtWidth: 1200,
                renderItemFn: renderContentType
            },
            {
                label: 'createdOn',
                displayLabel: 'Created',
                width: '.5fr',
                hideOnMobile: true,
                showAtWidth: 800,
                renderItemFn: renderCreated
            },
            {
                label: 'addedOn',
                displayLabel: 'Modified',
                width: '.5fr',
                renderItemFn: renderLastModified
            }
        ].filter(util.existsFilter) as EditableTablePropertyEntry<Document>[]
    }, [docs, documentsQuery.data, intl.d_documentHidden, renderContentType, renderCreated, renderFileName, renderFileSize, renderIcon, renderLastModified, renderPrivate]);

    const sortProps: EditableTableSortEntry<Document>[] = useMemo(() => {
        return [
            {label: 'addedOn', type: "number", order: 'desc'},
            {label: 'name', ignoreCase: true, type: "string", useFormatted: true},
            {label: 'fileSize', type: "number"},
        ]
    }, []);

    const filterComponent = useMemo(() => (
        <Searchbar
            keyName='doc-search'
            terms={searchTerms}
            options={searchOptions}
            onSearch={onSearch}
        />
    ), [searchTerms, searchOptions, onSearch]);

    const env = useMemo(() => ({
        dropFiles, hasChanged
    }), [dropFiles, hasChanged]);

    const editorProps = useMemo(() => ({
        containerHeight,
        containerWidth,
        isExpanded,
        parentId: props.entityId
    }), [containerHeight, containerWidth, isExpanded, props.entityId]);

    const startWithParam = useMemo(() => startWithNew ? 'file' : undefined, [startWithNew]);

    const items = useMemo(() => documentsQuery.data || docs, [docs, documentsQuery.data]);

    const name = useMemo(() => `Documents_${props.entityId}`, [props.entityId]);

    const DocumentTable = EditableTable as unknown as React.ComponentType<EditableTableProps<Document>>;

    return (
        <div className='document-block'
             onDrop={onDrop}
             onDragOver={onDragOver}
        >
            <Suspense fallback={<Loading/>}>
                <DocumentTable
                    name={name}
                    entityName='Document'
                    displayEntityName='Document'
                    urlQueryKey="document"
                    items={items}
                    embedded={props.embedded}
                    editor={DocumentEditor}
                    onAdd={onAdd}
                    canAdd={!props.readOnly}
                    canDeleteFn={canDelete}
                    canEditFn={canEdit}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onBeforeAdd={onBeforeEdit}
                    onBeginEdit={onBeforeEdit}
                    onAfterAdd={onEndEdit}
                    onEndEdit={onEndEdit}
                    customAddLabels={customAddLabels}
                    hideGridOnEdit={true}
                    properties={properties}
                    sortProps={sortProps}
                    editorProps={editorProps}
                    uniqueKey="fileName"
                    otherActions={otherActions}
                    filterComponent={filterComponent}
                    startWith={startWith ?? undefined}
                    startWithNew={startWithNew}
                    startWithParam={startWithParam}
                    env={env}
                    flowStyle={props.flowStyle}
                />
            </Suspense>
        </div>
    );
}

export type DocumentBlockProps = ContentBlockChildProps & Pick<RootState, 'documents'> & {
    // If set, automatically open this document when data is loaded (for deep linking)
    documentId?: string,

    // Is this embedded in a modal dialog?
    embedded?: boolean,

    // The id of this entity
    entityId: uuid,

    // Name of the entity, e.g. matter
    entityName: EntityNames,

    // Only allow file attachments
    filesOnly?: boolean,

    // Are we using the flow style?
    flowStyle?: boolean,

    // Callback fired right before an edit begins
    onBeforeEdit?: () => void,

    // Callback fired right after an edit ends
    onEndEdit?: () => void,

    // Called when a document is added, edited or removed
    onChange?: (docs: Document[], doc?: Document) => void,

    // Can the documents be edited?
    readOnly?: boolean,

    // Redux Documents array
    documents: ArrayMap<Document>,

    // Redux Misc state
    misc: MiscState,
};

const mapStateToProps = (state: RootState) => {
    return {
        documents: state.documents,

        // Need to keep tabs on these, as name changes will affect names we attach to documents
        campaigns: state.campaigns,
        contacts: state.contacts,
        leads: state.leads,
        matters: state.matters,
        misc: state.misc,
        tasks: state.tasks
    };
};

export default connect(mapStateToProps, null)(DocumentBlock);
